import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuGlobe, LuSmartphone, LuCloud, LuPalette, LuLightbulb, LuWrench, LuMapPin, LuUsers } from "react-icons/lu";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, company, emailaddress } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Images } from "../controller/Images";
import { useNavigate } from "react-router";
import "../styles/about-location.css";


// Same trust-logo set shown on /generative-ai-development — real clients,
// reused as-is rather than a page-specific set.
const TRUST_LOGOS = [
  //  // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

const APPROACH = [
  {
    num: "01 / PARTNER",
    title: "Business goals guide our work.",
    copy: "We begin with your users, workflows and priorities. These requirements guide the project scope, technology choices and delivery plan, so the software addresses a clear business need.",
    tags: ["Fixed-scope builds", "Dedicated teams", "MVP sprints"],
  },
  {
    num: "02 / DELIVER",
    title: "Clear stages. Shared expectations.",
    copy: "Our seven-stage process connects discovery, planning, design, content, development, testing and maintenance. Agreed milestones and review points help your team follow progress and make informed decisions.",
    tags: ["Discovery", "Design & build", "Testing & launch"],
  },
  {
    num: "03 / SUPPORT",
    title: "A plan for life after launch.",
    copy: "We plan for ongoing maintenance, security updates and product improvements. Support responsibilities, coverage and response expectations are defined around your application and agreed scope.",
    tags: ["Bug fixes", "Uptime monitoring", "Feature updates"],
  },
];

const CAPABILITIES = [
  { n: "01", t: "Web & Mobile", d: "Customer-facing applications, internal platforms and mobile apps built around your users, workflows and integration needs." },
  { n: "02", t: "Cloud & DevOps", d: "Cloud architecture, deployment pipelines and infrastructure automation on AWS and Azure, planned for your operating requirements." },
  { n: "03", t: "UI/UX Design", d: "User journeys, wireframes and prototypes that translate business requirements into clear, accessible interfaces." },
  { n: "04", t: "AI & Automation", d: "AI agents, generative AI and recommendation engines connected to your business systems, with defined permissions and appropriate review." },
];

const SERVICES = [
  { Icon: LuGlobe, title: "Web Development", copy: "Responsive websites and web applications, with performance, accessibility and system integrations considered from the start.", path: "/web-development", featured: true },
  { Icon: LuSmartphone, title: "Mobile App Development", copy: "iOS and Android applications built with native or cross-platform technologies to suit your users and feature requirements.", path: "/mobile-app-development" },
  { Icon: LuCloud, title: "Cloud and DevOps", copy: "Cloud infrastructure, deployment pipelines and monitoring that support consistent releases and ongoing operations.", path: "/devops-services" },
  { Icon: LuPalette, title: "UI/UX Design", copy: "User flows, interface designs and prototypes that help teams validate usability before development.", path: "/ui-ux-design" },
  { Icon: LuLightbulb, title: "Technology Consulting", copy: "Practical guidance on architecture, technology selection, modernization and implementation planning.", path: "/it-consulting" },
  { Icon: LuWrench, title: "Maintenance & Optimization", copy: "Maintenance, issue resolution and enhancements delivered through an agreed support scope.", path: "/product-support-maintenance" },
];

const FIT_CRITERIA = [
  { a: "What expertise does the project need?", b: "MATCH" },
  { a: "What delivery capacity is required?", b: "CAPACITY" },
  { a: "How do scope and dependencies affect timing?", b: "SCOPE" },
  { a: "Which engagement model fits your priorities?", b: "FIT" },
];

const FACTS = [
  { a: "3", b: "Engagement models" },
  { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" },
  { a: "11", b: "Core services offered" },
];

const ENGAGEMENT_RESULTS = [
  { a: "Fixed-Scope", b: "Clearly defined deliverable" },
  { a: "Dedicated Team", b: "Ongoing, evolving product work" },
  { a: "MVP Sprint", b: "Test core features with users" },
  { a: "1", b: "Coordinated delivery approach" },
];

const WHO_WE_BUILD_FOR = [
  { a: "Early-stage startups", b: "Define and build an MVP focused on essential features, user feedback and the next stage of development." },
  { a: "Growing SMBs", b: "Connect business systems and automate workflows as your team, operations and customer base grow." },
  { a: "Enterprises", b: "Modernize legacy applications and integrate complex systems through planned, documented delivery." },
  { a: "E-commerce brands", b: "Develop storefronts, payment integrations and inventory workflows that support the shopping experience." },
  { a: "SaaS product teams", b: "Extend your engineering capacity with developers working within your roadmap, tools and release process." },
  { a: "Agencies & consultancies", b: "Add development capacity for client projects, coordinated with your account and delivery teams." },
  { a: "Non-profits & public sector", b: "Build accessible applications with an agreed scope, practical budget and documentation for handover." },
  { a: "Internal platform teams", b: "Improve deployment automation, monitoring and cloud operations for your business applications." },
];

const FAQS = [
  {
    q: "Is InQmise Technologies a registered company?",
    a: "Yes. InQmise Technologies India Pvt Ltd is an MSME-registered IT company based in Delhi NCR, India.",
  },
  {
    q: "Where is InQmise Technologies located?",
    a: "Our office is at Cabin No. 10, Second Floor, A 24/9, Mohan Cooperative, Badarpur, New Delhi 110044.",
  },
  {
    q: "How does InQmise approach software development?",
    a: "We start by understanding your business requirements, then coordinate design, engineering and testing through a seven-stage delivery process. Technology decisions, project milestones and support requirements are discussed with your team.",
  },
];

const NEEDS = ["Build a new application", "Improve an existing platform", "Explore AI or cloud solutions", "Add engineering capacity", "Discuss a technology challenge"];
const NEXT_STEPS = [
  { n: "01", t: "Tell us about your business", d: "Share your idea, current systems and the challenge you want to solve." },
  { n: "02", t: "Meet the right specialists", d: "We review your inquiry and arrange a discussion around your technical and business needs." },
  { n: "03", t: "Explore how we can work together", d: "Discuss priorities, project scope and an engagement model that fits your team." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Same section-for-section structure as /generative-ai-development (hero +
// engine panel, trust logos, feature rows, process, service cards, a dark
// evaluation-style panel, an engagement-model spotlight, a "who we build
// for" grid, an inline FAQ, and a contact CTA with an embedded form) —
// applied to About with real, non-fabricated company content in each slot.
const About = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "About InQmise Technologies | Software & AI Company, Delhi NCR";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Learn about InQmise Technologies, a software and AI development company in Delhi NCR. Explore our expertise, process and engagement models.";
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = "IT company Delhi NCR, software development company Delhi, web development services, custom app development, AI integration services, digital transformation Delhi, tech consulting, cloud solutions, startup technology partner, agentic AI, RAG solutions, machine learning services, InQmise Technologies";
    document.head.appendChild(keywords);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/about");

    // The company entity (@id #organization) is declared once, in index.html.
    // This page used to restate it with its own url, description and
    // areaServed; every copy sharing that @id is merged by Google as the same
    // entity, so the conflicting copies made its details unpredictable.

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
    return () => {
      description.remove();
      keywords.remove();
      faqScript.remove();
    };
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (contactStatus === "submitting") return;
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      const firstInvalid = Object.keys(newErrors).find((name) => newErrors[name]);
      document.getElementById(`about-${firstInvalid}`)?.focus();
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai about-spacing pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">About InQmise Technologies</p>
          <h1>
            Building software. <em>Solving business challenges.</em>
          </h1>
          <p className="ea-lede">
            InQmise Technologies is a software and AI development company based in Delhi NCR. We help startups, growing businesses and enterprises build applications, connect systems and improve operations through custom software, cloud solutions and AI.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">
              Discuss your project ↗
            </button>
            <a className="ea-text-link" href="#approach">See how we work</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / COMPANY PROFILE <b>DELHI NCR</b></div>
          <h2>Design, engineering and delivery, working together.</h2>
          <div className="ea-query"><small>COMPANY</small>InQmise Technologies India Pvt Ltd</div>
          <div className="ea-query"><small>HEADQUARTERS</small>Delhi NCR, India</div>
          <div className="ea-flow"><b>Strategy</b><i>→</i><b>Build</b><i>→</i><b>Support</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>BUSINESSES WE HAVE WORKED WITH</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* APPROACH — feature rows */}
      <section className="ea-wrap ea-section" id="approach">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we operate</div>
            <h2>From business needs<br />to working software.</h2>
          </div>
          <p>We connect business understanding with technical delivery, from the initial requirements discussion to launch and ongoing support.</p>
        </Reveal>

        {APPROACH.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* CAPABILITIES — 4-step process grid */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Our expertise</div>
            <h2>Technical expertise<br />across your product lifecycle.</h2>
          </div>
          <p>Bring together the capabilities your project needs, with design, development and infrastructure planned as part of one delivery approach.</p>
        </Reveal>
        <div className="ea-process">
          {CAPABILITIES.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we build</div>
              <h2>Focused services.<br />Coordinated delivery.</h2>
            </div>
            <p>Engage us for a specific technical requirement or a complete development project, with scope and responsibilities agreed from the start.</p>
          </Reveal>
          <div className="ea-cards">
            {SERVICES.map((item, i) => (
              <Reveal
                as="a"
                i={(i % 4) + 1}
                reduceMotion={reduceMotion}
                key={item.title}
                className={`ea-card ${item.featured ? "ea-featured" : ""}`}
                href={item.path}
                onClick={(e) => { e.preventDefault(); navigate(item.path); }}
                style={{ textDecoration: "none" }}
              >
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIT CRITERIA + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Project fit</div>
              <h2>A clear scope starts<br />with the right questions.</h2>
            </div>
            <p>We assess requirements, team capacity and project dependencies before recommending a delivery approach and timeline.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>How we assess your project</h3>
              {FIT_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Engagement models</div>
            <h2>Three ways to work<br />with us.</h2>
          </div>
          <p>Choose an engagement model that reflects your requirements, roadmap and internal capacity.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">FIXED-SCOPE / DEDICATED / MVP</div>
            <h3>A delivery model that fits your project.</h3>
            <p>A fixed-scope engagement suits clearly defined requirements. A dedicated team provides ongoing capacity for an evolving roadmap. An MVP sprint focuses on a first release to test key features and gather feedback. Each model follows an agreed plan with clear responsibilities and review points.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss your project</button>
          </div>
          <div className="ea-results">
            {ENGAGEMENT_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* WHO WE BUILD FOR */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Who we build for</div>
            <h2>Support for your<br />next stage of development.</h2>
          </div>
          <p>We adapt the scope and delivery approach to your organization, existing systems and business priorities.</p>
        </Reveal>
        <div className="ea-industries">
          {WHO_WE_BUILD_FOR.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ea-wrap ea-section ab-location" aria-labelledby="about-location-heading">
        <div className="ab-location-heading"><span><LuMapPin aria-hidden="true" /> Where we work</span><h2 id="about-location-heading">Based in Delhi NCR.<br />Connected to your team.</h2><p>We support clients in India and internationally through remote collaboration, with local discussions arranged when appropriate for the engagement.</p></div>
        <div className="ab-location-layout">
          <div className="ab-location-address"><LuMapPin aria-hidden="true" /><h3>Our home base</h3><p>{company.street}<br />{company.locality}, {company.region} {company.postalCode}</p><div className="ab-location-areas">{["Delhi", "NCR", "Gurgaon", "Noida", "Faridabad", "Greater Noida", "Haryana", "Uttar Pradesh"].map((area) => <span key={area}>{area}</span>)}</div></div>
          <div className="ab-location-remote"><LuUsers aria-hidden="true" /><h3>Your team, wherever you work</h3><p>Scheduled discussions, written updates and agreed review milestones keep delivery coordinated around your team’s requirements.</p><p>Our portfolio includes Three Lock Box, a platform built for the US construction industry.</p><a className="ea-text-link" href="/case-studies" onClick={(e) => { e.preventDefault(); navigate("/case-studies"); }}>Explore our client projects →</a></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>About InQmise: common questions.</h2>
            <p>Learn more about our company, location and development approach.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `about-faq-panel-${i}`;
            const buttonId = `about-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <a className="ea-text-link" href="/faq" onClick={(e) => { e.preventDefault(); navigate("/faq"); }}>See the full FAQ</a>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <div className="hm-page about-inquiry">
      <section className="ea-cta" aria-labelledby="about-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <div className="about-inquiry-copy">
            <div className="ea-eyebrow">Work with InQmise</div>
            <h2 id="about-contact-heading">Bring your next idea to a team that understands your business.</h2>
            <p>Whether you need a new application, improvements to an existing platform or additional engineering capacity, start with a conversation about your goals.</p>
            <ol className="hm-next">
              {NEXT_STEPS.map((s) => (
                <li key={s.n}>
                  <span className="hm-next-n">{s.n}</span>
                  <div>
                    <b>{s.t}</b>
                    <p>{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="hm-direct">
              <a href={`tel:${company.phoneHref}`}>
                <span>Call</span>
                <b>{company.phone}</b>
              </a>
              <a href={`mailto:${emailaddress}`}>
                <span>Email</span>
                <b>{emailaddress}</b>
              </a>
            </div>
            <p className="hm-direct-note">{company.street}, {company.locality}, {company.region} {company.postalCode}</p>
          </div>

          <div className="ea-contact-card">
            <span>LET’S EXPLORE YOUR NEXT PROJECT</span>
            <h3>Tell us what you have in mind.</h3>
            <p>Share a short overview. Our team aims to respond within one business day.</p>

            <fieldset className="hm-needs">
              <legend>What kind of support are you looking for?</legend>
              <div className="hm-needs-row">
                {NEEDS.map((need) => (
                  <button
                    type="button"
                    key={need}
                    className={`hm-need${formData.subject === need ? " is-picked" : ""}`}
                    aria-pressed={formData.subject === need}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, subject: need }));
                      setContactErrors((prev) => ({ ...prev, subject: "" }));
                    }}
                  >
                    {need}
                  </button>
                ))}
              </div>
            </fieldset>

            <form onSubmit={handleContactSubmit} aria-label="About page project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="about-fullname">Full Name</label>
                <input
                  id="about-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "about-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="about-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="about-phone">Phone Number (optional)</label>
                  <input
                    id="about-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "about-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="about-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="about-email">Email</label>
                  <input
                    id="about-email" name="email" type="email" value={formData.email} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "about-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="about-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="about-subject">Subject</label>
                <input
                  id="about-subject" name="subject" type="text" placeholder="Pick one above, or write your own"
                  value={formData.subject} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "about-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="about-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="about-description">Tell us about your project</label>
                <textarea
                  id="about-description" name="description" rows={3} value={formData.description} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "about-description-error" : undefined}
                />
                {contactErrors.description && <p id="about-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Sending…" : "Connect with our team ↗"}
              </button>
              <p className="hm-form-note">We will review your inquiry and follow up to understand your requirements.</p>
            </form>
          </div>
        </div>
      </section>
      </div>
    </main>
  );
};

export default About;
